import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, Observable, tap, throwError } from 'rxjs';

import {
  ICreateUser,
  IUser,
  IUserLocalStorage,
  LoginErrorResponse,
} from '../common/models/auth.interface';
import { environment } from '../../environments/environment';
import { HttpService } from './http.service';
import { Role } from '../common/enums/role.enum';
import { ThemeService } from './theme.service';
import { BusyService } from './busy.service';
import { ClientsService } from './clients.service';
import { ClientResponse, IClientData } from '../common/models/client.interface';
import { AlertService } from './alert.service';

const url = environment.API_URL + '/auth/';
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private userData: string = 'userData';
  private _userData = new BehaviorSubject<IUserLocalStorage | null>(
    this.getUser(),
  );

  userData$ = this._userData.asObservable();

  constructor(
    private router: Router,
    private httpService: HttpService,
    private themeService: ThemeService,
    private busyService: BusyService,
    private clientService: ClientsService,
    private alertService: AlertService,
  ) {}

  signup(body: ICreateUser): Observable<ICreateUser> {
    return this.httpService.post(url + 'signup', body).pipe(
      catchError((error) => {
        console.error('Error during signup:', error);
        return throwError(() => error);
      }),
    );
  }

  decodificarManualmente(token: string) {
    try {
      const partes = token.split('.');
      if (partes.length !== 3) {
        throw new Error('Formato de token inválido');
      }

      const payload = partes[1];
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error al decodificar el token:', error);
      return null;
    }
  }

  login(body: {
    email: string;
    password: string;
  }): Observable<{ data: { accessToken: string; refreshToken: string } }> {
    return (
      this.httpService
        // health-check
        .post(url + 'signin', body)
        .pipe(
          tap(
            (response: {
              data: { accessToken: string; refreshToken: string };
            }) => {
              const { data } = response;
              if (data.accessToken) {
                localStorage.setItem('token', data.accessToken);
                const userData: IUser = this.decodificarManualmente(
                  data.accessToken,
                );
                this.loadClientData(userData);
              }
            },
          ),
          catchError((error: LoginErrorResponse) => {
            const errorMessage =
              error.error.status === 601
                ? error.error.data
                : 'Unexpected error';
            this.alertService.setErrorMessage(errorMessage);
            return throwError(() => error);
          }),
        )
    );
  }

  loadClientData(userData: IUser): void {
    this.clientService.getClientById(userData.clientId).subscribe({
      next: (response: ClientResponse) => {
        console.log('Client data loaded:', response.data);
        const { userId, email, clientId, roles } = userData;
        const clientData: IClientData = response.data;
        const role = roles[0];

        this.setUserData({
          id: userId,
          email,
          clientName: clientData.name,
          folder_name: clientData.folder_name,
          role,
          clientId,
          logo: clientData.logo,
          theme: {
            type: clientData.color_type,
            primaryColor: clientData.color_primary,
            secondaryColor: clientData.color_secondary,
            tertiaryColor: clientData.color_tertiary,
            textColor: clientData.color_text,
            formBg: clientData.color_tertiary,
          },
        });

        this.redirectByRole(role);
        this.themeService.applyTheme();
      },
      error: (error) => {
        console.error('Error loading client data', error);
      },
    });
  }

  setUserData(data: IUserLocalStorage): void {
    try {
      localStorage.setItem(this.userData, JSON.stringify(data));
      this._userData.next(data);
    } catch (error) {
      console.error('Error save data in local storage: ', error);
    }
  }

  getUser(): IUserLocalStorage | null {
    const data = localStorage.getItem(this.userData);
    if (data) {
      return JSON.parse(data);
    }
    return null;
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('token');
    if (!token) {
      return false;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000;
      return Date.now() < exp;
    } catch (error) {
      console.error('Error decoding token:', error);
      return false;
    }
  }

  logout(): void {
    this.busyService.busy();

    setTimeout(() => {
      localStorage.clear();
      this.router.navigate(['/']);
      this.themeService.destroy();
      this.busyService.idle();
    }, 2000);
  }

  redirectByRole(role: Role): void {
    switch (role) {
      case Role.SUPERVISOR:
        this.router.navigate(['/home/admin']);
        break;
      case Role.ADMIN:
        this.router.navigate(['/home/admin']);
        break;
      case Role.SUPER_ADMIN:
        this.router.navigate(['/home/admin']);
        break;
      default:
        this.router.navigate(['/home/admin']);
    }
  }
}
